import { normalizeName } from "./ggb-text.js";
import { emptyPlayer, has, holeNumbers, usableKinds } from "./ggb-types.js";
import type { ParseResult, ParsedPlayer } from "./ggb-types.js";

export const leaderboardMessage =
  "This screenshot only contains the leaderboard. Open the finished round in Golf GameBook and capture each player's scorecard with the hole numbers and scores visible.";
export const noScorecardMessage =
  "These screenshots don't contain complete hole-by-hole scorecards. Open the finished round in Golf GameBook and capture each player's scorecard with the hole numbers and scores visible.";

export interface ImportSummary {
  players: ParsedPlayer[];
  issues: string[];
}

/**
 * Combines all screenshots into one player list: front and back nines of
 * the same name merge, an identical card uploaded twice counts once, and two
 * different players from the same round are never treated as duplicates.
 * Returns the guidance message instead when nothing usable was found.
 */
export function summarizeResults(
  results: ParseResult[],
): ImportSummary | { error: string } {
  const usable = results.filter((result) => usableKinds.has(result.kind));
  if (usable.length === 0) {
    const guide = results
      .flatMap((result) => result.issues)
      .find((issue) => /hole-by-hole scorecard/i.test(issue));
    if (guide) return { error: guide };
    const leaderboard = results.some((result) => result.kind === "leaderboard");
    return { error: leaderboard ? leaderboardMessage : noScorecardMessage };
  }
  const players: ParsedPlayer[] = [];
  for (const result of usable) {
    for (const player of result.players) {
      if (holeNumbers(player.holes).length === 0) continue;
      // Equal strokes do not establish an unknown player's identity. Named
      // duplicates merge below so a clearer image can replace reconstruction.
      const existing = players.find(
        (known) =>
          player.name &&
          known.name &&
          normalizeName(known.name) === normalizeName(player.name),
      );
      const merged = existing ? mergePlayers(existing, player) : null;
      if (existing && merged) players[players.indexOf(existing)] = merged;
      else players.push(structuredClone(player));
    }
  }
  if (!players.length) return { error: noScorecardMessage };
  return { players, issues: results.flatMap((result) => result.issues) };
}

/** Merges two cards of the same player; conflicting holes become uncertain. */
export function mergePlayers(
  a: ParsedPlayer,
  b: ParsedPlayer,
): ParsedPlayer | null {
  if (a.name && b.name && normalizeName(a.name) !== normalizeName(b.name))
    return null;
  if (
    a.course &&
    b.course &&
    normalizeName(a.course) !== normalizeName(b.course)
  )
    return null;
  const merged: ParsedPlayer = {
    ...emptyPlayer(),
    ...(a.scoreFormat || b.scoreFormat
      ? { scoreFormat: a.scoreFormat ?? b.scoreFormat }
      : {}),
    ...(a.scoringBasis || b.scoringBasis
      ? { scoringBasis: a.scoringBasis ?? b.scoringBasis }
      : {}),
    points: { ...a.points, ...b.points },
    ...((a.stablefordTotal ?? b.stablefordTotal) !== undefined
      ? { stablefordTotal: a.stablefordTotal ?? b.stablefordTotal }
      : {}),
    visibleHoles: [
      ...new Set([
        ...(a.visibleHoles ?? holeNumbers(a.holes)),
        ...(b.visibleHoles ?? holeNumbers(b.holes)),
      ]),
    ].sort((a, b) => a - b),
    name: a.name ?? b.name,
    course: a.course ?? b.course,
    playedDate: a.playedDate ?? b.playedDate,
    hcp: a.hcp ?? b.hcp,
    out: a.out ?? b.out,
    inn: a.inn ?? b.inn,
    total: a.total ?? b.total,
    netTotal: a.netTotal ?? b.netTotal,
    reconstructed: Array.from(
      new Set([...a.reconstructed, ...b.reconstructed]),
    ),
    fingerprint: a.fingerprint || b.fingerprint,
  };
  for (const player of [a, b]) {
    for (const n of holeNumbers(player.holes)) {
      const value = player.holes[n]!;
      if (has(merged.holes, n) && merged.holes[n] !== value) {
        merged.conf[n] = 0;
      } else {
        merged.holes[n] = value;
        merged.conf[n] = Math.min(merged.conf[n] ?? 1, player.conf[n] ?? 1);
      }
    }
    for (const n of holeNumbers(player.par)) merged.par[n] = player.par[n]!;
    for (const n of holeNumbers(player.si)) merged.si[n] = player.si[n]!;
    for (const n of holeNumbers(player.net)) merged.net[n] = player.net[n]!;
  }
  merged.reconstructed = merged.reconstructed.filter(
    (n) =>
      ![a, b].some(
        (player) =>
          has(player.holes, n) &&
          !player.reconstructed.includes(n) &&
          player.holes[n] === merged.holes[n] &&
          (player.conf[n] ?? 1) > 0,
      ),
  );
  return merged;
}

export interface MatchCandidate {
  id: string;
  displayName: string;
}

export type PlayerMatch =
  | { kind: "exact" | "strong-token" | "close"; id: string }
  | { kind: "manual" }
  | { kind: "ambiguous" };

/** Finds the app participant a parsed name belongs to, when unambiguous. */
export function matchPlayer(
  rawName: string | null,
  candidates: readonly MatchCandidate[],
): PlayerMatch {
  const query = normalizeName(rawName);
  if (!query || query.replace(/[^a-z]/g, "").length < 3)
    return { kind: "manual" };
  const exact = candidates.filter(
    (candidate) => normalizeName(candidate.displayName) === query,
  );
  if (exact.length === 1) return { kind: "exact", id: exact[0]!.id };
  if (exact.length > 1) return { kind: "ambiguous" };
  const tokens = query.split(" ").filter((token) => token.length >= 2);
  if (tokens.length >= 2) {
    const hits = candidates.filter((candidate) => {
      const parts = normalizeName(candidate.displayName).split(" ");
      return tokens.every((token) => parts.includes(token));
    });
    if (hits.length === 1) return { kind: "strong-token", id: hits[0]!.id };
    if (hits.length > 1) return { kind: "ambiguous" };
    // Permit small OCR errors in full names only. Similar candidates still
    // require confirmation rather than choosing whichever occurs first.
    const close = candidates
      .map((candidate) => {
        const name = normalizeName(candidate.displayName);
        return { id: candidate.id, name, distance: nameDistance(query, name) };
      })
      .filter(
        ({ name, distance }) =>
          name.split(" ").length >= 2 &&
          distance <=
            Math.min(2, Math.floor(Math.max(query.length, name.length) * 0.15)),
      )
      .sort((a, b) => a.distance - b.distance);
    if (
      close.length &&
      (!close[1] || close[1].distance - close[0]!.distance >= 2)
    )
      return { kind: "close", id: close[0]!.id };
    if (close.length) return { kind: "ambiguous" };
  }
  return { kind: "manual" };
}

function nameDistance(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++)
      row[j] = Math.min(
        row[j - 1]! + 1,
        previous[j]! + 1,
        previous[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    previous = row;
  }
  return previous[b.length]!;
}
